/**
 * ActivityMap — full-canvas map for the OEM Home Dashboard.
 *
 * Two render modes (Phase 2):
 *  - RESTING (plotMode = null): plots aggregated Location pins (one per site),
 *    green, name-labelled, with a click popup of rollup counts. Consumes
 *    `useHomeLocations(range)`.
 *  - PLOTTING (plotMode = ASSET | ACTIVITY | ROUTINE): a card is selected, so the
 *    map plots that entity's individual points, rendered CLUSTERED (bounded but
 *    large for assets) and coloured by status. Consumes
 *    `useHomeEntityPoints(range, plotMode)`. ROUTINE plots the routine-bearing
 *    locations, aggregated.
 *
 * `searchQuery` filters the visible markers by name (client-side, case-insensitive).
 *
 * Web-only — do NOT import from mobile code.
 */

import { HomeEntityType } from '@gripp/shared-logic';
import { tokens } from '@gripp/shared-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import {
  useHomeLocations,
  type LocationSummary,
} from '../hooks/useHomeLocations';
import {
  useHomeEntityPoints,
  type HomeMapPoint,
} from '../hooks/useHomeEntityPoints';
import { type TimeRange } from '../types';
import { type MapMarker, MapView } from './mapView';

interface ActivityMapProps {
  range: TimeRange;
  /** Active plotting mode (null = resting Location view). */
  plotMode?: HomeEntityType | null;
  /** Case-insensitive name filter applied to the visible markers. */
  searchQuery?: string;
}

// Severity-derived activity status → Crop Circle semantic colour (matches the
// Activity card's Solved/Pending/Urgent palette).
const ACTIVITY_STATUS_COLORS: Record<string, string> = {
  urgent: tokens.colors.semantic.error,
  pending: tokens.colors.semantic.warning,
  solved: tokens.colors.semantic.success,
};

// Asset status → slate ramp (matches the Assets card donut).
const ASSET_STATUS_COLORS: Record<string, string> = {
  Deployed: tokens.colors.slate[500],
  'In Transit': tokens.colors.slate[400],
  'In Service': tokens.colors.slate[300],
  Returned: tokens.colors.slate[200],
};

/**
 * Converts a `LocationSummary` to a resting-view `MapMarker` — green pin with a
 * soft pulse and a rollup popup (Activities / Assets / Routines).
 */
function locationToMarker(location: LocationSummary): MapMarker {
  return {
    id: location.id,
    name: location.name,
    lat: location.lat,
    lng: location.lng,
    color: tokens.colors.semantic.success,
    pulse: true,
    details: [
      { label: 'Activities', value: String(location.activities.total) },
      { label: 'Assets', value: String(location.assets) },
      { label: 'Routines', value: String(location.routines) },
    ],
  };
}

/** Converts a plotting-mode `HomeMapPoint` to a coloured `MapMarker`. */
function entityPointToMarker(
  point: HomeMapPoint,
  mode: HomeEntityType
): MapMarker {
  const status = point.status ?? '';
  let color: string = tokens.colors.semantic.success;
  let bucket: MapMarker['bucket'];

  if (mode === HomeEntityType.Activity) {
    color = ACTIVITY_STATUS_COLORS[status] ?? tokens.colors.semantic.success;
    if (status === 'urgent' || status === 'pending' || status === 'solved') {
      bucket = status;
    }
  } else if (mode === HomeEntityType.Asset) {
    color = ASSET_STATUS_COLORS[status] ?? tokens.colors.slate[400];
  }

  return {
    id: point.id,
    name: point.name,
    lat: point.lat,
    lng: point.lng,
    color,
    bucket,
    // Routine points are aggregated locations — pulse like the resting pins.
    pulse: mode === HomeEntityType.Routine,
  };
}

const DEFAULT_CENTER: [number, number] = [0, 20]; // [lng, lat]
const DEFAULT_ZOOM = 1.5;

export function ActivityMap({ range, plotMode, searchQuery }: ActivityMapProps) {
  const { t } = useTranslation();

  const locationsQuery = useHomeLocations(range);
  const entityQuery = useHomeEntityPoints(range, plotMode ?? null);

  // Active source = entity points when a plotting mode is set, else Locations.
  const isPlotting = !!plotMode;
  const isLoading = isPlotting ? entityQuery.isLoading : locationsQuery.isLoading;
  const error = isPlotting ? entityQuery.error : locationsQuery.error;

  const markers = useMemo(() => {
    // Branch on `plotMode` (not the boolean) so TS narrows it to a non-null
    // HomeEntityType inside the plotting branch.
    const base = plotMode
      ? (entityQuery.data ?? []).map((p) => entityPointToMarker(p, plotMode))
      : (locationsQuery.data ?? []).map(locationToMarker);

    const q = searchQuery?.trim().toLowerCase();
    if (!q) return base;
    return base.filter((m) => m.name.toLowerCase().includes(q));
  }, [plotMode, entityQuery.data, locationsQuery.data, searchQuery]);

  // The basemap always renders; loading / error / empty are overlays on top.
  let status: { message: string; testID: string } | null = null;
  if (isLoading) {
    status = {
      message: t('home.map.loading', 'Loading map…'),
      testID: 'activity-map-loading',
    };
  } else if (error) {
    status = {
      message: t('home.map.error', 'Unable to load map data.'),
      testID: 'activity-map-error',
    };
  } else if (markers.length === 0) {
    status = {
      message: searchQuery?.trim()
        ? t('home.map.noResults', 'No matches for this search.')
        : t('home.map.empty', 'No locations for this period.'),
      testID: 'activity-map-empty',
    };
  }

  return (
    <View style={styles.fill} testID="activity-map">
      <MapView
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        markers={markers}
        fitToMarkers
        clustered={isPlotting}
      />
      {status && (
        <View
          style={styles.statusOverlay}
          pointerEvents="none"
          testID={status.testID}
        >
          <Text style={styles.statusText}>{status.message}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
  statusOverlay: {
    position: 'absolute',
    bottom: tokens.spacing.lg * 5, // above the card strip
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  statusText: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[600],
    backgroundColor: `${tokens.colors.brand.white}D9`, // ~85% opacity
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.borderRadius.full,
    overflow: 'hidden',
  },
});
